/**
 * Mobile navigation theme — the single source for the locked global nav system
 * (CLAUDE.md "LOCKED — global mobile nav system" / Nav Lock Final.dc.html).
 *
 * Consumed by the three nav surfaces (MobileLeagueNavigation, MobileTeamNavigation,
 * BottomNav) via the shared NavCell. The flat segmented control + line icons live
 * here so all three inherit from one source.
 *
 * Themeable: the active accent is var(--team-primary, #eab308) — never a hardcoded
 * gold literal in the components. Neutral chrome reads rgba(55,65,81,.5) borders.
 *
 * Unit 0 relocated this block out of the global colorService; it stays relocated
 * (no colorService import for nav styling). The #eab308 brand fallback lives only
 * inside the var(--team-primary, #eab308) token, never as a scattered literal.
 */
import {
  Home, Trophy, MessageSquare, Receipt, Activity, Briefcase,
  BarChart3, Users, CircleDot, LayoutGrid, History, Settings,
  Search, ArrowRightLeft, Target, Snowflake, Star, TrendingUp,
  Newspaper, User, Zap,
} from 'lucide-react';

const PRIMARY = 'var(--team-primary, #eab308)';
const BORDER = '1px solid rgba(55, 65, 81, 0.5)';

// The ACTUAL rendered height of the fixed BottomNav — the single source of truth
// so any overlay (player modal sheet, full-profile card) can end ABOVE the nav
// instead of running under it. Composition (must match BottomNav + NavCell):
//   bottomStrip padding-top 2px
//   + NavCell: padding 7px*2 (14) + icon 19 + gap 3 + label 9   = 45px
//   + bottomStrip padding-bottom 9px (BottomNav overrides to 9px + safe-area)
//   = 56px + env(safe-area-inset-bottom)
export const BOTTOM_NAV_HEIGHT = 'calc(56px + env(safe-area-inset-bottom))';

export const navTheme = {
  // Themeable accent + neutral inactive
  primary: PRIMARY,
  inactive: '#9ca3af',
  iconStroke: 1.8,            // locked range 1.7–1.9

  // Strips (the fixed/sticky bar chrome). No strip hairline + no side padding:
  // the control bleeds edge-to-edge as one seamless full-width bar (Tony override
  // of the prior locked border/padding values).
  topStrip: {
    background: 'linear-gradient(180deg, #202028, #16161c)',
    padding: '2px 0',
  },
  bottomStrip: {
    background: '#0b0b11',
    padding: '2px 0 9px',
  },

  // The shared segmented box. No border/radius — at edge-to-edge that border
  // would become the new inner hairline and round the screen-edge corners; with
  // it gone the bar is seamless. Background + overflow:hidden kept.
  segment: {
    background: '#0e0e14',
    overflow: 'hidden',
  },
  cellDivider: BORDER,        // left-divider between cells (skip the first) — kept

  // Per-surface cell variants. topCell is now IDENTICAL in size to bottomCell
  // (same iconSize/labelSize/gap; both share padding 7px 0 in NavCell) — size
  // parity is the requirement. Long league labels ("Trading Block",
  // "Transactions") may run tight at 8.5px in a 6-equal-cell full-width row;
  // that is accepted (clip/ellipsis), the font is NOT shrunk to compensate.
  // Only railPosition/railInset differ (underline at bottom vs rail at top).
  topCell:    { iconSize: 19, labelSize: '9px', labelWeight: 700, gap: '3px', railPosition: 'bottom', railInset: '20%' },
  bottomCell: { iconSize: 19, labelSize: '9px', labelWeight: 700, gap: '3px', railPosition: 'top',    railInset: '24%' },

  // Team-nav CLUBHOUSE → FRONT OFFICE group divider (a stacked-label cell).
  // Background derives from the themeable primary via color-mix (no gold literal).
  group: {
    minWidth: '46px',
    background: 'color-mix(in srgb, var(--team-primary, #eab308) 5%, transparent)',
    color: PRIMARY,
    labelSize: '7px',
    labelWeight: 900,
  },

  // Team-nav right-edge swipe fade (blends into the segment background)
  scrollFade: 'linear-gradient(90deg, rgba(14,14,20,0) 0%, #0e0e14 92%)',

  // 4px clearance between any fixed bar and the first container it meets (locked)
  clearance: '4px',
};

// Line-icon set — one lucide component per destination/tab. No emoji.
export const navIcons = {
  // League nav
  leagueHome: Home,
  standings: Trophy,
  messageBoard: MessageSquare,
  transactions: Receipt,
  liveScoring: Activity,
  tradingBlock: Briefcase,
  // Team nav — CLUBHOUSE
  dashboard: Home,
  stats: BarChart3,
  roster: Users,
  optimizer: Zap,
  probablePitchers: CircleDot,
  boxScores: LayoutGrid,
  lineupHistory: History,
  teamSetup: Settings,
  // Team nav — FRONT OFFICE
  freeAgents: Search,
  tradeCenter: ArrowRightLeft,
  draft: Target,
  offSeason: Snowflake,
  watchList: Star,
  minorLeague: TrendingUp,
  // Bottom nav
  home: Home,
  leagues: Trophy,
  teams: Users,
  news: Newspaper,
  account: User,
};

export default navTheme;
